import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, X } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'

const navItems: { key: RouteKey; labelKey: string }[] = [
  { key: 'about', labelKey: 'nav.about' },
  { key: 'products', labelKey: 'nav.products' },
  { key: 'sustainability', labelKey: 'nav.sustainability' },
  { key: 'news', labelKey: 'nav.news' },
  { key: 'contacts', labelKey: 'nav.contacts' },
]

export function Header() {
  const { t } = useTranslation('common')
  const location = useLocation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const headerRef = useRef<HTMLElement>(null)

  const pathSegments = location.pathname.split('/').filter(Boolean)
  const currentSlug = pathSegments[1] || ''

  const pagesWithLightTop: string[] = []
  const forceSolid = pagesWithLightTop.includes(currentSlug)

  // Detect scroll with smooth transition threshold
  useEffect(() => {
    let ticking = false
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50)
          ticking = false
        })
        ticking = true
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMobileOpen(false)
  }, [location.pathname])

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isMobileOpen])

  const isTransparent = !isScrolled && !forceSolid

  return (
    <header
      ref={headerRef}
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
        isTransparent
          ? 'bg-transparent'
          : 'bg-dark-deep/95 backdrop-blur-md shadow-lg'
      )}
    >
      {/* Top accent line with glow */}
      <div className="h-[2px] w-full bg-primary" style={{ boxShadow: '0 1px 8px rgba(40,160,219,0.3)' }} />

      <nav
        aria-label={t('aria.mainNavigation')}
        className="mx-auto flex max-w-site items-center justify-between px-container-x h-16 md:h-20"
      >
        {/* Logo with hover scale */}
        <LocalizedLink to="home" className="flex-shrink-0 transition-transform duration-300 hover:scale-105">
          <img
            src="/images/logos/ESEC_logo_Bomanpayoff_bianco.png"
            alt="Boman S.r.l."
            className={cn(
              'w-auto transition-all duration-500',
              isTransparent ? 'h-9 md:h-10' : 'h-8 md:h-9'
            )}
          />
        </LocalizedLink>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-0.5">
          {navItems.map(({ key, labelKey }) => {
            const routePath = localizedRoutes[key]
            const isActive = routePath.it === currentSlug || routePath.en === currentSlug

            return (
              <LocalizedLink
                key={key}
                to={key}
                className={cn(
                  'group relative px-4 py-2 font-heading text-xs uppercase tracking-[0.12em] transition-all duration-300',
                  'text-white/60 hover:text-white',
                  isActive && 'text-white'
                )}
              >
                {t(labelKey)}
                {/* Underline indicator with glow */}
                <span
                  className={cn(
                    'absolute bottom-0 left-4 right-4 h-[2px] bg-primary transition-all duration-300 origin-left',
                    isActive
                      ? 'scale-x-100 opacity-100'
                      : 'scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-100'
                  )}
                  style={isActive ? { boxShadow: '0 1px 6px rgba(40,160,219,0.4)' } : undefined}
                />
              </LocalizedLink>
            )
          })}
        </div>

        {/* Desktop Actions */}
        <div className="hidden lg:flex items-center gap-2">
          <LanguageSelector variant="desktop" isScrolled={!isTransparent} />
        </div>

        {/* Mobile Toggle — animated icon */}
        <button
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className={cn(
            'lg:hidden p-2 rounded-sm transition-all duration-300',
            'text-white/80 hover:text-white hover:bg-white/10',
            isMobileOpen && 'rotate-90'
          )}
          aria-label={isMobileOpen ? t('aria.closeMenu') : t('aria.openMenu')}
          aria-expanded={isMobileOpen}
        >
          {isMobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Mobile Navigation Overlay — staggered items */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden fixed inset-0 top-[calc(4rem+2px)] md:top-[calc(5rem+2px)] bg-dark-deep/98 backdrop-blur-sm z-40 overflow-y-auto"
          >
            <nav
              className="flex flex-col py-8"
              aria-label={t('aria.mobileNavigation')}
            >
              {/* Home link */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 }}
              >
                <LocalizedLink
                  to="home"
                  className={cn(
                    'px-8 py-4 font-heading text-sm uppercase tracking-[0.15em] transition-all duration-300 block',
                    currentSlug === ''
                      ? 'text-primary border-l-3 border-primary bg-white/5'
                      : 'text-white/60 hover:text-white hover:bg-white/5 hover:pl-10'
                  )}
                >
                  {t('nav.home')}
                </LocalizedLink>
              </motion.div>

              {navItems.map(({ key, labelKey }, index) => {
                const routePath = localizedRoutes[key]
                const isActive = routePath.it === currentSlug || routePath.en === currentSlug

                return (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * (index + 1) }}
                  >
                    <LocalizedLink
                      to={key}
                      className={cn(
                        'px-8 py-4 font-heading text-sm uppercase tracking-[0.15em] transition-all duration-300 block',
                        isActive
                          ? 'text-primary border-l-3 border-primary bg-white/5'
                          : 'text-white/60 hover:text-white hover:bg-white/5 hover:pl-10'
                      )}
                    >
                      {t(labelKey)}
                    </LocalizedLink>
                  </motion.div>
                )
              })}

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.35 }}
                className="border-t border-white/10 mt-8 pt-8 px-8"
              >
                <LanguageSelector variant="mobile" />
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
